export type Exercise = {
  id: string;
  exerciseId?: string;
  videoId?: string;
  name: string;
  shortName: string;
  duration: number;
  prescription: string;
  sets?: number | null;
  reps?: number | null;
  prescribedTime?: number | null;
  side?: string | null;
  cue: string;
  instructions: string[];
  posterUrl?: string | null;
  hlsUrl?: string | null;
  progressive?: Array<{
    height: number;
    width: number;
    type: string;
    link: string;
  }>;
  accent: "green" | "gold";
};

export type SessionProgram = {
  id: string;
  requestedId?: string;
  slug?: string | null;
  requestSource?: "programId" | "programSlug" | "default";
  name: string;
  sessionName?: string;
  description?: string;
  imageUrl?: string | null;
  dayOrder?: number;
  routineId?: string;
  weekId?: string;
};

export type SessionPayload = {
  program: SessionProgram;
  exercises: Exercise[];
};

export type MiniSessionRequest = {
  programId?: string;
  programSlug?: string;
  programName?: string;
};

export const downloadUrl = "https://prehabapp.onelink.me/mYpq/share";

export const program = {
  id: "8a81021798a31b900198a3f3737c01e0",
  name: "Prehab Program",
  totalDuration: "About 2 min",
};

export const exercises: Exercise[] = [
  {
    id: "quad-set",
    name: "Quad Set",
    shortName: "Quad Set",
    duration: 45,
    prescription: "1 set - 10 reps",
    sets: 1,
    reps: 10,
    side: "Both",
    cue: "Drive the back of your knee down, tighten the quad, then breathe.",
    instructions: [
      "Sit with your leg straight and a small towel under your knee.",
      "Gently press down into the towel until your thigh tightens.",
      "Hold for two slow breaths, then relax with control.",
    ],
    posterUrl: "/assets/session-controls-preview.webp",
    accent: "green",
  },
  {
    id: "heel-slide",
    name: "Heel Slide",
    shortName: "Heel Slide",
    duration: 45,
    prescription: "1 set - 8 reps",
    sets: 1,
    reps: 8,
    side: "Working leg",
    cue: "Slide only as far as you can stay smooth and comfortable.",
    instructions: [
      "Lie on your back with your heel on the floor.",
      "Slide the heel toward you while the knee bends.",
      "Pause, then slowly slide the leg back out.",
    ],
    posterUrl: "/assets/follow-along-session.webp",
    accent: "gold",
  },
];

const cleanParam = (value: string | null) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export function miniSessionRequestFromUrl(search = window.location.search): MiniSessionRequest {
  const params = new URLSearchParams(search);
  return {
    programId: cleanParam(params.get("programId")),
    programSlug: cleanParam(params.get("programSlug")),
    programName: cleanParam(params.get("programName")),
  };
}

function sessionApiUrl(request: MiniSessionRequest) {
  const params = new URLSearchParams();
  if (request.programId) params.set("programId", request.programId);
  if (request.programSlug) params.set("programSlug", request.programSlug);
  if (request.programName) params.set("programName", request.programName);
  const query = params.toString();
  return query ? `/api/session?${query}` : "/api/session";
}

export async function loadMiniSession(
  request: MiniSessionRequest = { programId: program.id, programName: program.name },
): Promise<SessionPayload> {
  const response = await fetch(sessionApiUrl(request), {
    headers: {
      accept: "application/json",
    },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.message || "Unable to load mini session.");
  }
  if (!body?.program || !Array.isArray(body.exercises) || body.exercises.length < 2) {
    throw new Error("Mini session did not include two exercises.");
  }
  return body as SessionPayload;
}
